import { validation } from '../components/input/validation';
import { FormContainer as FormContainerType, FormElement } from '../schema/form.schema';
import { useEffect, useState } from 'react';
import { InputElement } from '../schema/input.schema';
import { ChoiceElement } from '../schema/choice.schema';
import { ToggleElement } from '../schema/toggle.schema';
import { validateSelection } from '../components/choice/validation';
import { SwitchableElement, SwitchableOption } from '../schema/switchable.schema';
import { ContextElement } from '../schema/context.schema';

export interface FormValidationData {
	[key: string]: boolean;
}

export const useFormValidation = (schema: FormContainerType, onValidationChange: (isValid: boolean) => void) => {
	const [validationResults, setValidationResults] = useState<FormValidationData>(getFormBaseValidation(schema));

	useEffect(() => {
		const isValid = Object.values(validationResults).every((value) => value);
		onValidationChange(isValid);
	}, [validationResults, onValidationChange]);

	const onValidationResultChange = (key: string, value: boolean) => {
		let updatedResults = { ...validationResults, [key]: value };

		const switchableElements = schema.elements.filter(
			(element) => element.type === 'switchable'
		) as Array<SwitchableElement>;

		switchableElements.forEach((switchableElement) => {
			const selectedOption = switchableElement.options.find((option) => option.value === key);
			if (selectedOption) {
				switchableElement.options.forEach((option) => {
					if (option !== selectedOption) {
						updatedResults = deleteValidationRecursively(option.content, updatedResults);
					}
				});
			}
		});

		setValidationResults(updatedResults);

		let selectedSwitchableOption: SwitchableOption | undefined;
		switchableElements.some((switchableElement) => {
			const foundOption = switchableElement.options.find((option) => option.value == key);
			if (foundOption) {
				selectedSwitchableOption = foundOption;
				return true;
			}
			return false;
		});

		if (!selectedSwitchableOption) return;
		selectedSwitchableOption.content.forEach((element) => {
			getElementValidation(element, updatedResults);
		});

		const isValid = Object.values(updatedResults).every((result) => result);
		onValidationChange(isValid);
	};

	return { validationResults, onValidationResultChange };
};

const getElementValidation = (element: FormElement, validationResults: FormValidationData) => {
	if (isValidationElement(element)) {
		validationResults[element.key] = getElementBaseValidation(element);
	}
	if (isNodeValidationElement(element)) {
		if (element.type === 'context') {
			element.content.forEach((childElement) => {
				getElementValidation(childElement, validationResults);
			});
		}
		if (element.type === 'switchable') {
			const selectedOptions = element.options.find((option) => option.value === element.selected);
			if (!selectedOptions) return;
			selectedOptions.content.forEach((element) => {
				getElementValidation(element, validationResults);
			});
		}
	}
};

const getFormBaseValidation = (schema: FormContainerType): { [key: string]: boolean } => {
	const validationResults: FormValidationData = {};

	schema.elements.forEach((element) => {
		getElementValidation(element, validationResults);
	});

	return validationResults;
};

type ValidationElements = InputElement | ChoiceElement | ToggleElement;
type NodeValidationElements = SwitchableElement | ContextElement;

const VALIDATION_ELEMENTS = ['input', 'choice', 'toggle'];
const NODE_VALIDATION_ELEMENTS = ['switchable', 'context'];

const isValidationElement = (element: FormElement): element is ValidationElements => {
	return VALIDATION_ELEMENTS.includes(element.type);
};

const isNodeValidationElement = (element: FormElement): element is NodeValidationElements => {
	return NODE_VALIDATION_ELEMENTS.includes(element.type);
};

const getElementBaseValidation = (element: ValidationElements) => {
	switch (element.type) {
		case 'input':
			return validateInput(element);
		case 'choice':
			return validateChoice(element);
		case 'toggle':
			return validateToggle(element);
		default:
			throw new Error(`Unsupported validation element type: ${element}`);
	}
};

const validateInput = (element: InputElement) => {
	const isRequired = element.required ?? false;
	const prefillAvailable = element.prefill_value !== undefined;

	if (isRequired) {
		if (prefillAvailable) {
			const validationResult = validation(element.prefill_value, element.validator);
			return validationResult.isValid;
		} else {
			return false;
		}
	} else {
		if (prefillAvailable) {
			const validationResult = validation(element.prefill_value, element.validator);
			return validationResult.isValid;
		} else {
			return true;
		}
	}
};

const validateChoice = (element: ChoiceElement) => {
	const isRequired = element.required ?? false;
	const preselectedAvailable = element.preselected !== undefined;

	if (isRequired) {
		if (preselectedAvailable) {
			const validationResult = validateSelection(element.preselected, element.validator, element.required);
			return validationResult.valid;
		} else {
			return false;
		}
	} else {
		if (preselectedAvailable) {
			const validationResult = validateSelection(element.preselected, element.validator, element.required);
			return validationResult.valid;
		} else {
			return true;
		}
	}
};

const validateToggle = (element: ToggleElement) => {
	if (element.required) {
		return element.selected;
	} else {
		return true;
	}
};

const deleteValidationRecursively = (content: Array<FormElement>, data: FormValidationData) => {
	let updatedResults = data;

	content.forEach((element: FormElement) => {
		if (isValidationElement(element)) {
			delete updatedResults[element.key];
		}
		if (isNodeValidationElement(element)) {
			if (element.type === 'context') {
				deleteValidationRecursively(element.content, updatedResults);
			} else {
				element.options.forEach((option) => {
					deleteValidationRecursively(option.content, updatedResults);
				});
			}
		}
	});
	return updatedResults;
};
